import { Armchair, LogOut } from "lucide-react";
import { getCatalogItem, getHomeType, interiorCapacity, placementSizeFraction } from "@tribe/domain";
import { useVillageStore } from "./store";

/**
 * Arranging the inside of your home.
 *
 * The indoor twin of `YardEditor`, and deliberately the same shape: what is in here, what is left
 * to bring in. The differences are the ones that matter indoors — the space is finite and comes
 * from the building itself, so a bigger home is what buys more of it.
 *
 * Height is absent on purpose. Where a thing sits vertically is decided by what it is dragged over
 * in the scene, not by a control in a list; a number box for "how high" would be a worse answer to
 * "put the plant on the table" than dragging the plant onto the table.
 */
export function InteriorEditor() {
  const data = useVillageStore((state) => state.data);
  const selectedPlacementId = useVillageStore((state) => state.selectedPlacementId);
  const selectPlacement = useVillageStore((state) => state.selectPlacement);
  const toggleInteriorPlacement = useVillageStore((state) => state.toggleInteriorPlacement);
  const exitInterior = useVillageStore((state) => state.exitInterior);

  const homeType = data.property.homeType;
  const placed = data.property.placements.filter((placement) => placement.surface === "interior");
  const indoors = new Set(placed.map((placement) => placement.itemId));
  const available = data.catalog.filter(
    (item) => (item.category === "furniture" || item.category === "plant") &&
      data.ownedItemIds.includes(item.id) && !indoors.has(item.id)
  );

  if (!homeType) {
    return (
      <section className="panel-section first">
        <p className="socket-note">There is nothing to furnish until a home stands on your parcel.</p>
      </section>
    );
  }

  const capacity = interiorCapacity(homeType);
  const full = placed.length >= capacity;

  return (
    <>
      <section className="panel-section first">
        <div className="section-heading">
          <h3>Inside your {getHomeType(homeType).name.toLowerCase()}</h3>
          <span>{placed.length} of {capacity}</span>
        </div>

        {placed.length === 0 && (
          <p className="socket-note">Nothing in here yet. Bring something in from below.</p>
        )}

        <div className="placed-list">
          {placed.map((placement) => {
            const item = getCatalogItem(placement.itemId);
            const selected = placement.id === selectedPlacementId;
            return (
              <button
                key={placement.id}
                className={selected ? "is-selected" : ""}
                aria-pressed={selected}
                onClick={() => selectPlacement(selected ? null : placement.id)}
              >
                <Armchair size={17} />
                <span>{item.name}</span>
                <small>{Math.round(placementSizeFraction(placement.scaleStep) * 100)}%</small>
              </button>
            );
          })}
        </div>

        <p className="socket-note">Drag a piece over another to stand it on top.</p>
      </section>

      <section className="panel-section">
        <div className="section-heading">
          <h3>Ready to bring in</h3>
          <span>{available.length} in your collection</span>
        </div>
        {full && (
          <p className="socket-note">
            Your home is full. Take something out, or move somewhere with more room.
          </p>
        )}
        <div className="socket-list">
          {available.map((item) => (
            <button key={item.id} disabled={full} onClick={() => toggleInteriorPlacement(item.id)}>
              <Armchair size={17} /><span>{item.name}</span><small>Place</small>
            </button>
          ))}
        </div>
        {available.length === 0 && (
          <p className="socket-note">Everything you own that belongs indoors is already in here.</p>
        )}
      </section>

      <section className="panel-section">
        <button className="secondary-button" onClick={() => exitInterior()}>
          <LogOut size={17} /> Step outside
        </button>
      </section>
    </>
  );
}
